import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { translate } from './translate.js';
import { setActiveLanguage, formatNumber, formatDate, formatMoney } from './format.js';

const LANG_KEY = 'lang';
// English and Arabic cover every screen and are saved to the user's profile. Tagalog, Hindi and Malayalam cover the
// field-facing screens (scan, quick view, sync, audit walk, first walk, blind counts, location sheet, loaners, sign-in,
// mobile tab bar); other screens fall back to English. The server only stores en/ar, so a field language is kept on
// this device (the profile is set to English, the fallback for screens a field language does not cover).
export const SUPPORTED = [
  { code: 'en', label: 'English', dir: 'ltr', available: true },
  { code: 'ar', label: 'العربية', dir: 'rtl', available: true },
  { code: 'tl', label: 'Tagalog', dir: 'ltr', available: true, field: true },
  { code: 'hi', label: 'हिन्दी', dir: 'ltr', available: true, field: true },
  { code: 'ml', label: 'മലയാളം', dir: 'ltr', available: true, field: true },
];
const CODES = SUPPORTED.filter((l) => l.available).map((l) => l.code);
const FIELD_CODES = SUPPORTED.filter((l) => l.field).map((l) => l.code);
const SERVER_CODES = ['en', 'ar'];

const LanguageContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};

const initial = () => {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    return CODES.includes(stored) ? stored : 'en';
  } catch {
    return 'en';
  }
};

export function LanguageProvider({ children }) {
  const { user, updateUser } = useAuth();
  const [language, setLang] = useState(initial);

  // The signed-in user's saved preference wins over the device default.
  useEffect(() => {
    // A device-only field language (tl/hi/ml) is kept while the saved profile language is English.
    const pref = user?.preferred_language;
    if (pref && SERVER_CODES.includes(pref)) setLang((cur) => (pref === 'en' && FIELD_CODES.includes(cur) ? cur : pref));
  }, [user?.preferred_language]);

  const dir = language === 'ar' ? 'rtl' : 'ltr';
  setActiveLanguage(language);

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, language);
    } catch {
      /* storage blocked: the choice still applies for this visit */
    }
    document.documentElement.lang = language;
    document.documentElement.dir = dir;
  }, [language, dir]);

  const setLanguage = useCallback(
    async (code) => {
      if (!CODES.includes(code)) return;
      setLang(code);
      if (user) {
        const saved = SERVER_CODES.includes(code) ? code : 'en';
        try {
          await api.setMyLanguage(saved);
          updateUser({ preferred_language: saved });
        } catch {
          /* the choice still applies on this device */
        }
      }
    },
    [user, updateUser],
  );

  const t = useCallback((text, vars) => translate(language, text, vars), [language]);

  const value = useMemo(
    () => ({ language, dir, isRtl: dir === 'rtl', setLanguage, t, formatNumber, formatDate, formatMoney }),
    [language, dir, setLanguage, t],
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Account-menu language chooser: English / Arabic for every screen, Tagalog / Hindi / Malayalam for the field screens. */
export function LanguagePicker({ id = 'language-picker', className = 'form-select form-select-sm' }) {
  const { language, setLanguage, t } = useLanguage();
  return (
    <select
      id={id}
      className={className}
      aria-label={t('Interface language')}
      value={language}
      onChange={(e) => setLanguage(e.target.value)}
      onClick={(e) => e.stopPropagation()}
    >
      {SUPPORTED.map((l) => (
        <option key={l.code} value={l.code} disabled={!l.available}>
          {l.label}
          {!l.available ? ` (${t('Coming in Phase 2')})` : l.field ? ` (${t('field screens')})` : ''}
        </option>
      ))}
    </select>
  );
}
